import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSchemas } from "@/contexts/SchemaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Save, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import FieldListEditor from "@/components/schema/FieldListEditor";
import ImportExport from "@/components/schema/ImportExport";
import { useReviewFlow } from "@/components/schema/ReviewFlow";
import { fmtMeta } from "@/lib/schema";

const H = "text-xs uppercase tracking-[0.18em] text-purple-700 mb-3";
const LABEL = "text-xs uppercase tracking-[0.15em] text-slate-600";
const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

// Admin > Structure > Modules
export default function StructureModules() {
  const { reload, patient } = useSchemas();
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(null);     // {index, module}
  const domains = (patient?.domains && patient.domains.length ? patient.domains : (patient?.patientInputs || []).filter((g) => g.type === "group" && g.panel !== "patientFields").map((g) => g.key));

  const load = async () => {
    try {
      const { data } = await api.get("/schema/modules");
      setDoc(data.data); setMeta(data.Meta); setDirty(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const flow = useReviewFlow(() => { load(); reload(); });
  const edit = (patch) => { setDoc({ ...doc, ...patch }); setDirty(true); };
  const save = () => flow.start("Save module definitions", ({ dryRun, allowOlder }) =>
    api.put("/schema/modules", doc, { params: { dry_run: dryRun, allow_older: allowOlder } }));

  const saveModule = (m) => {
    const list = [...doc.modules];
    if (editing.index === -1) list.push(m); else list[editing.index] = m;
    edit({ modules: list });
    setEditing(null);
  };

  if (!doc) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="structure-modules">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-500" data-testid="structure-modules-meta">{fmtMeta(meta)}</div>
        <div className="flex flex-wrap gap-2">
          <ImportExport
            exportUrl="/schema/modules/export" filename="NeuroModuleDefinitions.json"
            importCall={({ dryRun, allowOlder }, json) => api.post("/schema/modules/import", json, { params: { dry_run: dryRun, allow_older: allowOlder } })}
            resetCall={({ dryRun }) => api.post("/schema/modules/reset", null, { params: { dry_run: dryRun } })}
            history={{ kind: "schema", recordId: "modules" }}
            onChanged={() => { load(); reload(); }}
          />
          <Button onClick={save} disabled={!dirty} className="btn-brand rounded-full" data-testid="structure-save"><Save className="h-4 w-4 mr-2" /> Review and save</Button>
        </div>
      </div>

      <div className="panel rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={H + " mb-0"}>Modules</div>
          <Button size="sm" variant="outline" data-testid="add-module"
            onClick={() => setEditing({ index: -1, module: { id: "", name: "", note: "", domains: [], status: "planned", panels: [], tbd: false, levelColumns: [], levels: [] } })}>
            <Plus className="h-4 w-4 mr-1" /> Add module
          </Button>
        </div>
        <div className="divide-y divide-slate-100">
          {doc.modules.map((m, i) => (
            <div key={m.id} className="py-2 flex items-center gap-3" data-testid={`module-row-${m.id}`}>
              <span className="font-mono text-sm font-medium w-36 truncate">{m.id}</span>
              <span className="text-sm text-slate-700 flex-1 truncate">{m.name}</span>
              {(m.domains || []).map((d) => <Badge key={d} variant="outline" className="text-[10px]">{d}</Badge>)}
              {m.tbd && <Badge variant="outline" className="text-[10px]">tbd</Badge>}
              <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
              <Button variant="ghost" size="sm" onClick={() => setEditing({ index: i, module: m })}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => edit({ modules: doc.modules.filter((_, k) => k !== i) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="panel rounded-2xl p-5">
        <div className={H}>Module inputs (the per-assignment form)</div>
        <FieldListEditor fields={doc.moduleInputs} allowGroups={false} allowArc={false} moduleInputs domains={domains}
          onChange={(moduleInputs) => edit({ moduleInputs })} />
      </div>

      <div className="panel rounded-2xl p-5">
        <div className={H}>Outputs (telemetry keys shown in results)</div>
        <OutputsEditor outputs={doc.outputs} domains={domains} onChange={(outputs) => edit({ outputs })} />
      </div>

      {editing && <ModuleDialog initial={editing.module} isNew={editing.index === -1} domains={domains}
        onClose={() => setEditing(null)} onSave={saveModule} />}
      {flow.dialog}
    </div>
  );
}

function OutputsEditor({ outputs, domains, onChange }) {
  const set = (i, patch) => onChange(outputs.map((o, k) => (k === i ? { ...o, ...patch } : o)));
  return (
    <div>
      <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">
        <span className="col-span-3">Key</span><span className="col-span-3">Label</span><span className="col-span-2">Domain</span>
        <span className="col-span-1">Unit</span><span className="col-span-1">Planned</span>
      </div>
      {outputs.map((o, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center mb-1.5" data-testid={`output-row-${o.key}`}>
          <Input className="col-span-3 font-mono h-8" value={o.key || ""} onChange={(e) => set(i, { key: e.target.value })} />
          <Input className="col-span-3 h-8" value={o.label || ""} onChange={(e) => set(i, { label: e.target.value })} />
          <select className="col-span-2 h-8 rounded-md border border-input bg-transparent text-sm px-2" value={o.domain || ""}
            onChange={(e) => set(i, { domain: e.target.value })}>
            <option value="">—</option>{domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <Input className="col-span-1 h-8" value={o.unit || ""} onChange={(e) => set(i, { unit: e.target.value })} />
          <div className="col-span-1"><Switch checked={!!o.planned} onCheckedChange={(v) => set(i, { planned: v })} /></div>
          <Button variant="ghost" size="sm" className="col-span-1" onClick={() => onChange(outputs.filter((_, k) => k !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="mt-1" data-testid="add-output"
        onClick={() => onChange([...outputs, { key: "", label: "", domain: domains[0] || "", unit: "", type: "float" }])}>
        <Plus className="h-4 w-4 mr-1" /> Add output
      </Button>
    </div>
  );
}

function ModuleDialog({ initial, isNew, domains, onClose, onSave }) {
  const [m, setM] = useState({
    ...initial, panelsText: (initial.panels || []).join(", "), colsText: (initial.levelColumns || []).join(", "),
    customSettings: (initial.customSettings || []).map((c) => ({ ...c, optionsText: (c.options || []).join(", ") })),
  });
  const set = (k, v) => setM({ ...m, [k]: v });
  const toggleDomain = (d) => set("domains", m.domains.includes(d) ? m.domains.filter((x) => x !== d) : [...m.domains, d]);
  const cols = csv(m.colsText);
  const getCell = (row, ci) => (Array.isArray(row) ? row[ci] : row[cols[ci]]);
  const setCell = (r, ci, v) => set("levels", m.levels.map((row, i) => {
    if (i !== r) return row;
    if (Array.isArray(row)) return Object.assign([...row], { [ci]: v });
    const was = row[cols[ci]];
    return { ...row, [cols[ci]]: typeof was === "number" && v.trim() !== "" && !Number.isNaN(Number(v)) ? Number(v) : v };
  }));
  const objectRows = m.levels.some((r) => !Array.isArray(r));
  const blankRow = (n) => (objectRows ? { Level: n, ...Object.fromEntries(cols.map((c) => [c, ""])) } : cols.map(() => ""));

  const submit = () => {
    const out = { ...m, panels: csv(m.panelsText), levelColumns: cols,
      levels: m.levels.map((row) => (Array.isArray(row) ? cols.map((_, c) => row[c] ?? "") : row)) };
    delete out.panelsText; delete out.colsText;
    out.customSettings = (m.customSettings || []).filter((c) => c.Key && c.Label).map(({ optionsText, ...c }) => {
      const o = csv(optionsText || "");
      if (c.type === "enum") return o.length ? { ...c, options: o } : { ...c, type: undefined };
      return c;                                    // bool / number / range / string keep min, max, unit, default, help
    });
    onSave(out);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="module-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "New module" : `Edit ${initial.id}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className={LABEL}>Id</Label><Input className="font-mono" value={m.id} disabled={!isNew} onChange={(e) => set("id", e.target.value)} data-testid="md-id" />
            <div className="text-[10px] text-slate-400 mt-1">Used by simulations (module_id) and the headset. Cannot change later.</div></div>
          <div><Label className={LABEL}>Name</Label><Input value={m.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="md-name" /></div>
          <div className="col-span-2"><Label className={LABEL}>Note</Label><Textarea rows={2} value={m.note || ""} onChange={(e) => set("note", e.target.value)} /></div>
          <div className="col-span-2"><Label className={LABEL}>What it is for (shown to therapists)</Label>
            <Textarea rows={3} value={m.purpose || ""} onChange={(e) => set("purpose", e.target.value)} data-testid="md-purpose" /></div>
          <div className="col-span-2"><Label className={LABEL}>Domains</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {domains.map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={m.domains.includes(d)} onChange={() => toggleDomain(d)} data-testid={`md-domain-${d}`} /> {d}
                </label>
              ))}
            </div></div>
          <div><Label className={LABEL}>Status</Label><Input value={m.status || ""} onChange={(e) => set("status", e.target.value)} /></div>
          <div className="flex items-end gap-3 pb-2"><Switch checked={!!m.tbd} onCheckedChange={(v) => set("tbd", v)} data-testid="md-tbd" />
            <span className="text-sm">TBD (common inputs plus Extra only)</span></div>
          <div className="col-span-2"><Label className={LABEL}>Panels (comma separated)</Label><Input value={m.panelsText} onChange={(e) => set("panelsText", e.target.value)} /></div>
          <div className="col-span-2"><Label className={LABEL}>Level columns (comma separated)</Label><Input value={m.colsText} onChange={(e) => set("colsText", e.target.value)} data-testid="md-cols" /></div>
        </div>
        {cols.length > 0 && (
          <div>
            <Label className={LABEL}>Levels (row 1 = level 1)</Label>
            <div className="mt-2 space-y-1">
              {m.levels.map((row, r) => (
                <div key={r} className="flex gap-1 items-center">
                  {cols.map((c, ci) => <Input key={ci} className="h-8" placeholder={c} value={getCell(row, ci) ?? ""} onChange={(e) => setCell(r, ci, e.target.value)} />)}
                  <Button variant="ghost" size="sm" onClick={() => set("levels", m.levels.filter((_, k) => k !== r))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => set("levels", [...m.levels, blankRow(m.levels.length + 1)])}><Plus className="h-4 w-4 mr-1" /> Add level</Button>
            </div>
          </div>
        )}
        <div>
          <Label className={LABEL}>Difficulty guide shown to therapists (what each level means)</Label>
          <div className="mt-2 space-y-1">
            <div className="grid grid-cols-6 gap-1 text-[10px] uppercase text-slate-400"><span>Setting</span>{[1, 2, 3, 4, 5].map((l) => <span key={l}>Level {l}</span>)}</div>
            {(m.difficultyGuide || []).map((row, r) => (
              <div key={r} className="grid grid-cols-6 gap-1 items-center">
                <Input className="h-8" value={row.label} onChange={(e) => set("difficultyGuide", m.difficultyGuide.map((x, i) => (i === r ? { ...x, label: e.target.value } : x)))} />
                {[0, 1, 2, 3, 4].map((i) => (
                  <Input key={i} className="h-8" value={row.values[i] ?? ""}
                    onChange={(e) => set("difficultyGuide", m.difficultyGuide.map((x, k) => (k === r ? { ...x, values: Object.assign([...x.values], { [i]: e.target.value }) } : x)))} />
                ))}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => set("difficultyGuide", [...(m.difficultyGuide || []), { label: "", values: ["", "", "", "", ""] }])}>
              <Plus className="h-4 w-4 mr-1" /> Add setting row
            </Button>
          </div>
        </div>
        <div>
          <Label className={LABEL}>Options therapists can set per patient (same at every level)</Label>
          <p className="text-[11px] text-slate-500 mt-1">Each becomes a control on the patient's activity card: a switch, a list of choices, or a free box until you define the values. The key is what the app reads.</p>
          <div className="mt-2 space-y-1">
            {(m.customSettings || []).map((c, i) => {
              const upd = (patch) => set("customSettings", m.customSettings.map((x, k) => (k === i ? { ...x, ...patch } : x)));
              const kind = c.type === "string" || !c.type ? "text" : c.type;
              return (
                <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`md-option-${c.Key}`}>
                  <Input className="col-span-4 h-8" placeholder="Label" value={c.Label || ""} onChange={(e) => upd({ Label: e.target.value })} />
                  <Input className="col-span-3 h-8 font-mono" placeholder="Key" value={c.Key || ""} onChange={(e) => upd({ Key: e.target.value })} />
                  <select className="col-span-2 h-8 rounded-md border border-input bg-transparent text-xs px-1" value={kind}
                    onChange={(e) => upd({ type: e.target.value === "text" ? undefined : e.target.value })}>
                    <option value="text">Free text</option><option value="bool">Switch</option><option value="enum">Choices</option><option value="number">Number</option><option value="range">Range</option>
                  </select>
                  {kind === "enum" ? <Input className="col-span-2 h-8" placeholder="A, B, C" value={c.optionsText || ""} onChange={(e) => upd({ optionsText: e.target.value })} />
                    : kind === "number" || kind === "range" ? <span className="col-span-2 flex gap-1"><Input type="number" className="h-8" placeholder="min" value={c.min ?? ""} onChange={(e) => upd({ min: e.target.value === "" ? undefined : Number(e.target.value) })} /><Input type="number" className="h-8" placeholder="max" value={c.max ?? ""} onChange={(e) => upd({ max: e.target.value === "" ? undefined : Number(e.target.value) })} /></span>
                    : <span className="col-span-2" />}
                  <Button variant="ghost" size="sm" className="col-span-1" onClick={() => set("customSettings", m.customSettings.filter((_, k) => k !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              );
            })}
            <Button size="sm" variant="outline" onClick={() => set("customSettings", [...(m.customSettings || []), { Key: "", Label: "" }])}><Plus className="h-4 w-4 mr-1" /> Add option</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="btn-brand rounded-full" onClick={submit} data-testid="md-save">{isNew ? "Add" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
